import { useCallback, useState } from "react";

import { Alert, Breadcrumb, Button, Page, Typography } from "@eisox/design-system";
import { SunIcon } from "@eisox/icons";
import { useQuery, useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { boilerRoomApi, gatewayApi, moduleApi } from "~/api";
import { HistoryDatesSlider } from "~/components/history-dates-slider";
import * as LiveHistoryToggle from "~/components/live-history-toggle";
import { Mode } from "~/components/live-history-toggle";
import { PositioningDialog, type Item } from "~/components/positioning-dialog";
import { RealtimeUnavailableDialog } from "~/components/realtime-unavailable-dialog";
import {
  ActionsDropdownMenu,
  BoilerRoom as BoilerRoomComponent,
  BoilerRoomProvider,
  DisplayDropdownMenu,
  ErrorCollapsible,
  RightColumn,
  Settings,
  useBoilerRoomContext,
} from "~/features/boiler-room";
import { useUpdateBoilerRoom } from "~/features/boiler-room/hooks/use-update-boiler-room.hook";
import { LiveErrorDialog } from "~/features/real-time";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import {
  isSameHistoryDate,
  planHistorySearchSchema,
  toPlanHistorySearch,
  type HistoryDateChangeOptions,
} from "~/helpers/history-search.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/boiler-rooms/$boilerRoomId")({
  validateSearch: planHistorySearchSchema,
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "boilerroom", buildingId, preload);
  },
  loader: async ({ context: { queryClient }, params: { buildingId } }) => {
    const boilerRooms = await queryClient.ensureQueryData(boilerRoomApi.queries.building(buildingId));
    await Promise.all(
      boilerRooms.map(boilerRoom => queryClient.ensureQueryData(boilerRoomApi.queries.boilerRoom(boilerRoom.id))),
    );
  },
  head: async ({ match: { context }, params: { buildingId, boilerRoomId } }) => {
    const boilerRooms = await context.queryClient.ensureQueryData(boilerRoomApi.queries.building(buildingId));
    return buildingPageHead(
      context.queryClient,
      buildingId,
      boilerRooms.find(boilerRoom => boilerRoom.id === boilerRoomId)?.name ?? m.boiler_room(),
    );
  },
  component: RouteComponent,
});

function BoilerRoom() {
  const { boilerRoomId } = Route.useParams();
  const { building, floors, permissions } = useBuildingContext(Route.id);

  // `boilerRooms` from the boiler-room context is the full (polled/historical) list — RightColumn needs it.
  const {
    boilerRooms,
    errors,
    layout,
    mode,
    onModeChange,
    isHistoryDatesLoading,
    isRealtimeStale,
    liveSampleDate,
    date,
    days,
    dates,
    onDateChange,
    onReload,
  } = useBoilerRoomContext(Route.id);

  const { mutate } = useUpdateBoilerRoom();

  const { data: modules = [] } = useQuery(moduleApi.queries.building(building.id));
  const { data: gateways = [] } = useQuery(gatewayApi.queries.building(building.id));
  const { data: buildingBoilerRooms = [] } = useQuery(boilerRoomApi.queries.building(building.id));
  const moduleId = buildingBoilerRooms.find(b => b.id === boilerRoomId)?.moduleId;
  const module = modules.find(m => m.id === moduleId);
  const gatewayId = module?.gatewayId;
  const gatewaySoftwareVersion = gateways.find(g => g.id === gatewayId)?.softwareVersion;

  const [positioningDialogOpen, setPositioningDialogOpen] = useState(
    boilerRooms.filter(b => b.floor === undefined).length > 0 && !!permissions.module?.boilerroom?.floor?.update,
  );

  const currentBoilerRoom = boilerRooms.find(b => b.id === boilerRoomId);
  if (!currentBoilerRoom) throw new Error("Boiler room not found");
  const floor = floors.find(f => currentBoilerRoom.floor?.id === f.id);

  const handleSubmit = (data: Item[]) => {
    data.forEach(item => {
      mutate({
        boilerRoomId: item.id,
        body: {
          ...(item.floor?.id !== undefined && { floorId: item.floor.id }),
          ...(item.floor?.x !== undefined && { x: item.floor.x }),
          ...(item.floor?.y !== undefined && { y: item.floor.y }),
        },
      });
    });
  };

  return (
    <Page.Root>
      <Page.Header className="flex-nowrap [&>:not(:first-child)]:shrink-0">
        <Breadcrumb.Root className="min-w-0 overflow-hidden">
          <Breadcrumb.List className="min-w-0">
            {floor && (
              <>
                <Breadcrumb.Item className="shrink-0">
                  <Breadcrumb.Link asChild>
                    <Link
                      to={"/buildings/$buildingId/floors/$floorId"}
                      params={{ buildingId: building.id, floorId: floor.id }}
                    >
                      {floor.name}
                    </Link>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator className="shrink-0" />
              </>
            )}
            <Breadcrumb.Item className="min-w-0">
              <Breadcrumb.Page asChild>
                <Page.Title asChild>
                  <Typography className="min-w-0">{currentBoilerRoom.name}</Typography>
                </Page.Title>
              </Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>
        {mode === Mode.Live && <Settings />}
        <Button
          asChild
          className="ml-auto flex-nowrap whitespace-nowrap text-primary-blue no-underline visited:text-primary-blue hover:text-primary-blue focus:text-primary-blue"
        >
          <Link to={"/buildings/$buildingId/settings/buildings/holidays"} params={{ buildingId: building.id }}>
            <div className="h-4 w-4 rounded-xs bg-planning-frost-free" />
            {m.frost_free_periods()}
          </Link>
        </Button>
        <Button asChild>
          <Link
            className="text-primary-blue no-underline visited:text-primary-blue hover:text-primary-blue focus:text-primary-blue"
            to={"/buildings/$buildingId/settings/buildings/season"}
            params={{ buildingId: building.id }}
          >
            <SunIcon />
            {m.season()}
          </Link>
        </Button>
        <DisplayDropdownMenu buildingId={building.id} gatewaySoftwareVersion={gatewaySoftwareVersion} />
        <ActionsDropdownMenu buildingId={building.id} history={mode === Mode.History} permissions={permissions} />
      </Page.Header>
      <Page.Content className="mb-10 ml-10 gap-5 md:mt-0 md:mr-0 md:grid-cols-[1fr_290px] grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] grid-rows-[auto_1fr] rounded-l-[10px] rounded-r-none bg-transparent [grid-template-areas:'scheme_error'_'scheme_column'_'scheme_liveHistoryToggle']">
        <div className="min-w-0 flex h-full w-full flex-col items-center justify-center rounded-[10px] bg-primary-white [grid-area:scheme]">
          {currentBoilerRoom.errors?.length && module ? (
            <Alert severity="error">
              <div className="flex flex-col">
                <Typography as="h3">{m.bms_modules_error()}</Typography>
                <LiveErrorDialog errors={currentBoilerRoom.errors} module={module} boilerRoom={currentBoilerRoom}>
                  <Button className="font-medium text-primary-white">{m.see_errors()}</Button>
                </LiveErrorDialog>
              </div>
            </Alert>
          ) : (
            <BoilerRoomComponent key={`${boilerRoomId}x${layout}`} buildingId={building.id} />
          )}
          {mode === Mode.History && (
            <HistoryDatesSlider
              isLoading={isHistoryDatesLoading}
              days={days}
              dates={dates}
              date={date}
              onDateCommit={onDateChange}
            />
          )}
        </div>
        <ErrorCollapsible className="[grid-area:error]" buildingId={building.id} errors={errors} />
        <RightColumn
          className="[grid-area:column]"
          buildingId={building.id}
          boilerRoomId={boilerRoomId}
          boilerRooms={boilerRooms}
        />
        <LiveHistoryToggle.Root
          className="gap-2 p-3 md:p-5 flex flex-col items-center justify-center rounded-[10px] bg-primary-white [grid-area:liveHistoryToggle]"
          mode={mode}
          onModeChange={onModeChange}
        >
          <LiveHistoryToggle.Tabs />
          <div className="gap-2.5 flex items-center">
            <LiveHistoryToggle.Timestamp className="md:text-base text-sm whitespace-nowrap" date={date} />
            <LiveHistoryToggle.Reload onReload={onReload} />
          </div>
        </LiveHistoryToggle.Root>
      </Page.Content>
      <PositioningDialog
        open={positioningDialogOpen}
        onOpenChange={setPositioningDialogOpen}
        buildingId={building.id}
        type="boilerRooms"
        items={boilerRooms}
        floors={floors}
        onSubmit={handleSubmit}
      />
      <RealtimeUnavailableDialog
        open={isRealtimeStale && !currentBoilerRoom.errors?.length}
        buildingId={building.id}
        lastSampleDate={liveSampleDate}
        onGoToHistory={() => onModeChange(Mode.History)}
      />
    </Page.Root>
  );
}

function RouteComponent() {
  const { buildingId, boilerRoomId } = Route.useParams();
  const { mode, date } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: boilerRooms } = useSuspenseQuery(boilerRoomApi.queries.building(buildingId));
  const boilerRoomsQueries = useSuspenseQueries({
    queries: boilerRooms.map(boilerRoom => boilerRoomApi.queries.boilerRoom(boilerRoom.id)),
  });
  const fullBoilerRooms = boilerRoomsQueries.map(query => query.data);

  const handleModeChange = useCallback(
    async (nextMode: Mode) => {
      await navigate({
        search: toPlanHistorySearch(nextMode, nextMode === Mode.History ? date : undefined),
        resetScroll: false,
      });
    },
    [date, navigate],
  );

  const handleDateChange = useCallback(
    async (nextDate: string, options?: HistoryDateChangeOptions) => {
      await navigate({
        search: prev => {
          if (isSameHistoryDate(prev.date, nextDate) && prev.mode === Mode.History) {
            return prev;
          }
          return toPlanHistorySearch(Mode.History, nextDate);
        },
        replace: options?.replace,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const onModeChange = (nextMode: Mode) => {
    void handleModeChange(nextMode);
  };

  const onDateChange = (nextDate: string, options?: HistoryDateChangeOptions) => {
    void handleDateChange(nextDate, options);
  };

  return (
    <BoilerRoomProvider
      boilerRooms={fullBoilerRooms}
      boilerRoomId={boilerRoomId}
      mode={mode}
      historyDate={date}
      onModeChange={onModeChange}
      onDateChange={onDateChange}
    >
      <BoilerRoom />
    </BoilerRoomProvider>
  );
}
